import { useCallback, useId, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Notifications } from '@mui/icons-material';
import {
  Badge,
  Button,
  Divider,
  IconButton,
  List,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import { sumBy } from 'lodash-es';
import { bindPopover, bindTrigger, usePopupState } from 'material-ui-popup-state/hooks';

import { NotificationsItem } from '@/features/notifications/components/NotificationsItem';
import { useNotifications } from '@/features/notifications/hooks/useNotifications';

export const NotificationsButton = () => {
  const { formatMessage } = useIntl();
  const popupId = useId();
  const popupState = usePopupState({ variant: 'popover', popupId });
  const notifications = useNotifications();
  const unreadCount = useMemo(
    () => sumBy(notifications, ({ isLoading, isUnread }) => (isUnread && !isLoading ? 1 : 0)),
    [notifications]
  );
  const markAllAsRead = useCallback(() => {
    notifications.forEach(({ markAsRead }) => {
      markAsRead();
    });
  }, [notifications]);
  return (
    <>
      {notifications.length > 0 && (
        <IconButton {...bindTrigger(popupState)} size="large" color="inherit">
          <Badge badgeContent={unreadCount} variant="dot" color="error">
            <Notifications titleAccess={formatMessage({ defaultMessage: 'Notifications' })} />
          </Badge>
        </IconButton>
      )}
      <Popover
        {...bindPopover(popupState)}
        // This closes the menu via event bubbling.
        onClick={popupState.close}
        anchorOrigin={{ horizontal: 'center', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'center', vertical: 'top' }}
        slotProps={{ paper: { style: { maxHeight: 480, width: 400 } } }}
      >
        <Stack direction="row" gap={2} p={2} justifyContent="space-between">
          <Typography variant="subtitle1" component="h2">
            <FormattedMessage defaultMessage="What's New" />
          </Typography>
          <Button variant="text" color="primary" onClick={markAllAsRead}>
            <FormattedMessage defaultMessage="Mark all as read" />
          </Button>
        </Stack>
        <Divider />
        <List disablePadding>
          {notifications.map((notification, index, array) => (
            <NotificationsItem
              key={notification.key}
              divider={index < array.length - 1}
              {...notification}
            />
          ))}
        </List>
      </Popover>
    </>
  );
};
